// src/components/layout/FloatingGlassContainer.tsx
import { Box, alpha, useMediaQuery, useTheme } from "@mui/material";

type FloatingGlassContainerProps = React.ComponentProps<typeof Box> & {
  /**
   * Choose the visual style:
   * - "glass": Columbia-blue plasmorphic glass (radial gradient + blur + soft white border)
   * - "dark":  Dark plate with highlighted border (no fill or very low-opacity dark wash)
   */
  variant?: "glass" | "dark";

  /**
   * For "dark" variant only: set to true to make the background fully transparent
   * (i.e., no fill at all). If false, a very low-opacity dark wash is used.
   */
  darkTransparent?: boolean;

  /**
   * Opacity factor for the glass variant.
   *  - 0 → fully transparent
   *  - 1 → baseline as designed
   *  - >1 → denser glass
   */
  glassOpacityFactor?: number;

  /**
   * Opacity factor for the dark variant.
   *  - 0 → fully transparent
   *  - 1 → baseline as designed
   *  - >1 → denser dark plate
   */
  darkOpacityFactor?: number;
};

const FloatingGlassContainer = ({
  children,
  sx,
  variant = "glass", // default to glass
  darkTransparent = false, // default: keep a tiny dark wash; set true to omit
  glassOpacityFactor = 0.95, // default glass strength
  darkOpacityFactor = 1.2, // default dark strength
  ...props
}: FloatingGlassContainerProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ---------------------------
  // GLASS (plasmorphic) variant
  // ---------------------------
  const glassBg = isDark
    ? `${alpha(theme.palette.secondary.main, 0.45 * glassOpacityFactor)}`
    : `${alpha(theme.palette.secondary.main, 0.45 * glassOpacityFactor)}`;

  const glassBorder = alpha(theme.palette.common.white, 0.35 * glassOpacityFactor);
  const glassBorderHover = alpha(theme.palette.common.white, 0.5 * glassOpacityFactor);

  // ---------------------------
  // DARK variant (highlighted border)
  // ---------------------------
  const darkBaseOpacity = (isDark ? 0.1 : 0.06) * darkOpacityFactor;
  const darkHoverOpacity = (isDark ? 0.14 : 0.08) * darkOpacityFactor;

  const darkBg = darkTransparent
    ? "transparent"
    : `linear-gradient(180deg,
        ${alpha(theme.palette.primary.dark, darkBaseOpacity)} 0%,
        ${alpha(theme.palette.primary.dark, darkBaseOpacity)} 100%)`;

  // Columbia Blue edge highlight on dark variant
  const darkBorder = isDark
    ? alpha(theme.palette.secondary.main, 0.55 * darkOpacityFactor)
    : alpha(theme.palette.primary.dark, 0.35 * darkOpacityFactor);

  const darkBorderHover = isDark
    ? alpha(theme.palette.secondary.main, 0.75 * darkOpacityFactor)
    : alpha(theme.palette.primary.dark, 0.45 * darkOpacityFactor);

  // ---------------------------
  // Resolve current variant
  // ---------------------------
  const resolvedBg = variant === "glass" ? glassBg : darkBg;
  // const hoverBg = variant === "glass" ? glassHoverBg : darkHoverBg;
  const hoverBg = variant === "glass" ? glassBg : darkBg;
  const borderColor = variant === "glass" ? glassBorder : darkBorder;
  const hoverBorder = variant === "glass" ? glassBorderHover : darkBorderHover;

  const boxShadow =
    theme.custom?.glassShadow || "0 10px 30px rgba(0, 56, 101, 0.22)";

  const navbarHeight = theme.custom?.navbarHeight ?? 64;

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        justifyContent: "center",
        overflowY: "auto",
        alignItems: isMobile ? "stretch" : "flex-start",
        py: isMobile ? theme.spacing(0.5) : theme.spacing(3),
        minHeight: `calc(100vh - ${navbarHeight}px)`,
      }}
    >
      <Box
        sx={{
          width: isMobile ? "100%" : "70%",
          background: resolvedBg, // ✅ always use `background` so gradients apply
          borderRadius: theme.shape.borderRadius / 2, // tied to theme for consistency
          boxShadow,
          border: `1px solid ${borderColor}`,
          // Blur:
          // - in "glass" → frosted effect
          // - in "dark"  → subtle depth
          backdropFilter: "blur(12px)",
          transition:
            "transform .25s cubic-bezier(0.4,0,0.2,1), box-shadow .25s ease, background .25s ease, border-color .25s ease",
          display: "flex",
          flexDirection: "column",
          p: isMobile ? theme.spacing(2) : theme.spacing(4),
          minHeight: `calc(100vh - ${navbarHeight + 48}px)`,
          color: theme.custom?.contentText?.primary || theme.palette.text.primary, // enforce readable text inside

          "&:hover": {
            background: hoverBg, // preserve gradient/wash on hover
            // transform: "translateY(-1px)",
            boxShadow: theme.custom?.glassShadow || boxShadow,
            borderColor: hoverBorder,
          },

          "&:active": {
            transform: "translateY(0)",
            boxShadow:
              theme.custom?.neumorphismInsetShadow ||
              `inset 0 2px 6px ${alpha(theme.palette.primary.dark, 0.2)}`,
          },

          "&:focus-visible": {
            outline: "none",
            boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.4)}`,
          },

          ...sx,
        }}
        {...props}
      >
        {children}
      </Box>
    </Box>
  );
};

export default FloatingGlassContainer;
